/**
 * Wizard card for the vercel_domain + generate_dns_email + verify_dns steps
 * (Slice 2 — the Domain group). One card (rendered for vercel_domain) drives all
 * three rows, mirroring StapeCard's "one card per group" shape:
 *
 *   Step 1  Add Vercel domain  → onboard-vercel-domain (add_domain)
 *   Step 2  Generate DNS email → generate-dns-email (generate) + Copy + optional Send
 *   Step 3  Verify DNS         → generate-dns-email (check_now) + a live per-record
 *                                table fed by the verify_dns task result. Manual:
 *                                the admin clicks "Refresh" to re-check (no cron).
 *
 * State is DERIVED from the task props (no setState-in-effect) — the sibling
 * generate_dns_email / verify_dns / stape_domain statuses come from the run
 * page's task list via the `allTasks` prop. No secret is handled here.
 */
import { useState } from 'react'
import { CheckCircle2, Circle, Copy, Loader2, RefreshCw, Send, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { useToast } from '@/hooks/use-toast'
import { StepCard } from './StepCard'

interface DomainCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
  allTasks?: OnboardingTask[]
}

interface DnsRecord {
  type: string
  host: string
  value: string
  domain: string
  resolved?: boolean
}

interface VercelResult {
  bookDomain?: string
  cnameTarget?: string
  reused?: boolean
  fallback?: boolean
}

interface EmailResult {
  subject?: string
  html?: string
  text?: string
  records?: DnsRecord[]
  sentTo?: string | null
  sentAt?: string | null
}

interface VerifyResult {
  records?: DnsRecord[]
  lastCheckedAt?: string
}

function RecordsTable({
  records,
  showResolved,
}: {
  records: DnsRecord[]
  showResolved?: boolean
}) {
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-xs">
        <thead className="bg-muted">
          <tr>
            <th className="px-2 py-1 text-left font-medium">Type</th>
            <th className="px-2 py-1 text-left font-medium">Host</th>
            <th className="px-2 py-1 text-left font-medium">Value</th>
            {showResolved && (
              <th className="px-2 py-1 text-left font-medium">Resolved</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y font-mono">
          {records.map((r) => (
            <tr key={`${r.host}.${r.domain}`}>
              <td className="px-2 py-1 uppercase">{r.type}</td>
              <td className="px-2 py-1">
                {r.host}.{r.domain}
              </td>
              <td className="px-2 py-1">{r.value}</td>
              {showResolved && (
                <td className="px-2 py-1">
                  {r.resolved ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Circle className="h-4 w-4 text-muted-foreground" />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function DomainCard({ task, accountId, runId, allTasks }: DomainCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)
  const [sendTo, setSendTo] = useState('')

  // Derive every display value from props — no setState-in-effect.
  const params = (task.params ?? {}) as { mainDomain?: string }
  const mainDomain = params.mainDomain ?? '{domain}'

  const vercelResult = (task.result ?? {}) as VercelResult
  const vercelDone = task.status === 'done'
  const vercelNeedsReview = task.status === 'needs_review'

  const byStep = new Map((allTasks ?? []).map((t) => [t.step, t]))
  const stapeTask = byStep.get('stape_domain')
  const emailTask = byStep.get('generate_dns_email')
  const verifyTask = byStep.get('verify_dns')

  const stapeReady = stapeTask?.status === 'done'
  const emailResult = (emailTask?.result ?? {}) as EmailResult
  const emailRecords = emailResult.records ?? []
  const emailDone = emailTask?.status === 'done'

  const verifyResult = (verifyTask?.result ?? {}) as VerifyResult
  const verifyRecords = verifyResult.records ?? []
  const verifyDone = verifyTask?.status === 'done'

  const canGenerate = (vercelDone || vercelNeedsReview) && stapeReady

  const addDomain = () =>
    invokeStep.mutate(
      {
        functionName: 'onboard-vercel-domain',
        accountId,
        params: { action: 'add_domain' },
      },
      {
        onError: (error) =>
          toast({
            title: 'Add Vercel domain failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )

  const generateEmail = () =>
    invokeStep.mutate(
      { functionName: 'generate-dns-email', accountId, params: { action: 'generate' } },
      {
        onError: (error) =>
          toast({
            title: 'Generate DNS email failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )

  const sendEmail = () => {
    if (!sendTo) return
    invokeStep.mutate(
      { functionName: 'generate-dns-email', accountId, params: { action: 'send', to: sendTo } },
      {
        onSuccess: () => toast({ title: `DNS email sent to ${sendTo}` }),
        onError: (error) =>
          toast({
            title: 'Send DNS email failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  }

  const checkNow = () =>
    invokeStep.mutate(
      { functionName: 'generate-dns-email', accountId, params: { action: 'check_now' } },
      {
        onError: (error) =>
          toast({
            title: 'DNS check failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailResult.text ?? '')
      toast({ title: 'DNS email copied to clipboard' })
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' })
    }
  }

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>Manual path if the automation cannot reach Vercel or the registrar:</p>
          <ol className="list-decimal space-y-1 pl-4">
            <li>
              In Vercel, add <code>book.{mainDomain}</code> to the viewer project and
              read its recommended CNAME target.
            </li>
            <li>
              In the client&rsquo;s DNS, add three CNAME records:{' '}
              <code>book → (Vercel target)</code>, <code>br → usa.stape.io</code>,{' '}
              <code>load.br → lusa.stape.io</code>.
            </li>
          </ol>
          <p>
            Prerequisites: the <code>VERCEL_TOKEN</code> / <code>VERCEL_PROJECT_ID</code>{' '}
            (and optional <code>VERCEL_TEAM_ID</code>) platform secrets; and the{' '}
            <code>stape_domain</code> step must have run first (it produces the br /
            load.br records).
          </p>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        {/* Step 1 — Add Vercel domain */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase text-muted-foreground">
            Step 1 — Add Vercel domain
          </p>
          {vercelDone || vercelNeedsReview ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                    {vercelResult.bookDomain ?? `book.${mainDomain}`}
                  </code>{' '}
                  → CNAME{' '}
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                    {vercelResult.cnameTarget ?? '—'}
                  </code>
                  {vercelResult.reused ? ' (reused)' : ''}
                </span>
              </div>
              {vercelNeedsReview && (
                <p className="text-xs text-orange-700">
                  Vercel did not return a recommended CNAME via the API — confirm the
                  exact target in the Vercel dashboard for this project (a fallback
                  value is shown above).
                </p>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addDomain}
                disabled={invokeStep.isPending}
              >
                {invokeStep.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Re-run
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-muted-foreground">
                Adds <code>book.{mainDomain}</code> to the viewer project and returns
                the CNAME target the client must point <code>book</code> at.
              </p>
              <Button type="button" size="sm" onClick={addDomain} disabled={invokeStep.isPending}>
                {invokeStep.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                Add domain
              </Button>
            </div>
          )}
        </div>

        {/* Step 2 — Generate DNS email */}
        <div className="space-y-2 border-t pt-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">
            Step 2 — DNS email
          </p>
          {!stapeReady && (
            <p className="text-xs text-amber-700">
              Run the Stape step first — it produces the br / load.br records this
              email needs.
            </p>
          )}
          {emailDone && emailRecords.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                The 3 records the client must add to DNS:
              </p>
              <RecordsTable records={emailRecords} />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant="outline" onClick={copyEmail}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={generateEmail}
                  disabled={invokeStep.isPending}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Regenerate
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="email"
                  placeholder="client@example.com"
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  className="h-8 max-w-xs text-xs"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={sendEmail}
                  disabled={invokeStep.isPending || !sendTo}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Send to client
                </Button>
                {emailResult.sentTo && (
                  <span className="text-xs text-green-700">
                    Sent to {emailResult.sentTo}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={generateEmail}
              disabled={!canGenerate || invokeStep.isPending}
            >
              {invokeStep.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Generate DNS email
            </Button>
          )}
        </div>

        {/* Step 3 — Verify DNS */}
        <div className="space-y-2 border-t pt-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">
            Step 3 — Verify DNS
          </p>
          {verifyRecords.length > 0 ? (
            <RecordsTable records={verifyRecords} showResolved />
          ) : (
            <p className="text-xs text-muted-foreground">
              No checks run yet. Once the client sets the records, click Refresh
              to check whether they resolve.
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={verifyDone ? 'outline' : 'default'}
              onClick={checkNow}
              disabled={!emailDone || invokeStep.isPending}
            >
              {invokeStep.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
            {verifyDone && (
              <span className="flex items-center gap-1 text-xs text-green-700">
                <CheckCircle2 className="h-4 w-4" /> All records resolve
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            DNS can take minutes to hours to propagate — click Refresh to re-check.
          </p>
        </div>
      </div>
    </StepCard>
  )
}
